import React, { useCallback, useMemo, useState } from 'react';
import Table from '../common/Table';
import { Grid } from '@mui/material';

import WorkOrderTableToolbar from './WorkOrdersTableToolbar';
import WorkOrdersTableNoRowsOverlay from './WorkOrdersTableNoRowsOverlay';
import ExpandableText from '../common/ExpandableText';
import { formatPhoneNumber } from '../../helpers/phoneFormatter';
import { woStatusToIcon } from '../../helpers/woStatusHelper';
import { formatWorkOrderSearch } from '../../helpers/woHelper';
import useStore from '../../hooks/useStore';

const renderExpandableCell = (params) => {
    return (
        <ExpandableText
            text={params.value || ''}
            width={params.colDef.computedWidth}
            lineClamp={1}
            isHeader={false}
        />
    );
};

const renderExpandableHeader = (params) => {
    return (
        <ExpandableText
            text={params.colDef.headerName}
            width={params.colDef.computedWidth}
            isHeader={true}
        />
    );
};

const columns = [
    {
        headerName: 'Status',
        field: 'Status',
        flex: 0.59,
        headerAlign: 'center',
        renderCell: (params) => (
            <div className='w-full flex justify-center'>
                {params.value ? woStatusToIcon[params.value] : woStatusToIcon['OPN']}
            </div>
        ),
        renderHeader: renderExpandableHeader,
    },    
    {
        headerName: 'Customer Name',
        field: 'Name',
        flex: 1.4,
        renderCell: renderExpandableCell,
        renderHeader: renderExpandableHeader,
    },
    {
        headerName: 'Phone Number',
        field: 'Phone',
        flex: 0.7,
        renderCell: (params) => renderExpandableCell(formatPhoneNumber(params)),
        renderHeader: renderExpandableHeader,
    },
    {
        headerName: 'Searchable Phone',
        field: 'SearchablePhone'
    },
    {
        headerName: 'Vehicle Year / Make / Model',
        field: 'YearMakeModel',
        flex: 1.7,
        renderCell: renderExpandableCell,
        renderHeader: renderExpandableHeader,
    },
    {
        headerName: 'Work Order',
        field: 'WorkOrder',
        flex: 0.8,
        renderCell: renderExpandableCell,
        renderHeader: renderExpandableHeader,
        getApplyQuickFilterFn: (value) => {
            if (!value) {
                return null;
            }
            return (params) => {
                const cleanWOinput = formatWorkOrderSearch(value);
                return cleanWOinput.test(Number(params.value || 0).toString())
            };
        },
    },
    {
        headerName: 'Time Expected Date',
        field: 'Appointment',
        flex: 0.8,
        renderCell: renderExpandableCell,
        renderHeader: renderExpandableHeader,
    },
];

const WorkOrderTable = ({
    workOrders,
    goToSafetyPrint,
    index,
    showDate = false,
    searchPlaceholder,
    setIndex,
    filterModel,
    setFilterModel
}) => {

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const [selectedStatuses, setSelectedStatuses] = useState([]);

    const { selectedStoreConfiguration } = useStore();

    const columnVisibilityModel = useMemo(() =>
    ({
        SearchablePhone: false,
        Appointment: showDate,
        Status: selectedStoreConfiguration.isStatusInspectionEnabled,
    }),
        [showDate, selectedStoreConfiguration.isStatusInspectionEnabled]);

    const rows = useMemo(() => {
        const filteredOrders = selectedStatuses.length === 0  || !selectedStoreConfiguration.isStatusInspectionEnabled? 
            workOrders : 
            workOrders.filter(order => selectedStatuses.includes(order.Status));
        
        return filteredOrders.map((row, idx) => ({
            id: row.id || idx,
            ...row,
        }));
    }, [workOrders, selectedStatuses, selectedStoreConfiguration.isStatusInspectionEnabled]);

    const getOtherTab = useCallback(() => {
        return index === 0 ? "All Other Work Orders" : "Today's Work Orders";
    }, [index]);

    const redirectToOtherTab = useCallback(() => {
        setIndex(index === 0 ? 1 : 0);
    }, [index, setIndex]);

    const emptySearch = useCallback(() => {
        setFilterModel({ items: [], quickFilterValues: [] });
    }, []);

    const CustomNoRowsOverlay = useCallback(
        () => (
            <WorkOrdersTableNoRowsOverlay
                redirectToOtherTab={redirectToOtherTab}
                emptySearch={emptySearch}
                filterModel={filterModel}
                getOtherTab={getOtherTab}
            />
        ),
        [filterModel]);

    const CustomWorkOrderTableToolbar = useCallback(
        () => (
            <WorkOrderTableToolbar
                searchPlaceholder={searchPlaceholder}
                
                onStatusChange={setSelectedStatuses}
                isStatusInspectionEnabled={selectedStoreConfiguration.isStatusInspectionEnabled}
            />
        ),
        [searchPlaceholder, selectedStoreConfiguration.isStatusInspectionEnabled]);


    const sx = useMemo(() => ({
        '& .MuiDataGrid-main ': {
            margin: '1em',
            overflow: 'hidden',
            border: 'none'
        },
        '.MuiDataGrid-virtualScroller': {
            overflow: 'hidden'
        },
        '.MuiDataGrid-toolbarContainer': {
            margin: '1em',
        },
        '.MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgb(242, 243, 244)',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
        },
        '.MuiDataGrid-overlayWrapperInner': {
            alignContent: 'center',
            border: 'none'
        },
        '.MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel': {
            marginTop: '1em',
            marginBottom: '1em'
        },
        '.MuiDataGrid-footerContainer': {
            borderTop: 'none'
        }
    }), []);

    return (
        <div className="safety-check-table-container">
            <Grid container>
                <Grid item xs={12} style={{ padding: 0, minHeight: 500 }}>
                    <Table
                        columns={columns}
                        columnVisibilityModel={columnVisibilityModel}
                        data={rows}
                        pagination={true}
                        rowsPerPageOptions={[10, 20, 30, 40, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
                        onRowClick={goToSafetyPrint}
                        customToolbar={CustomWorkOrderTableToolbar}
                        customNoRowsOverlay={CustomNoRowsOverlay}
                        rowHeight={45}
                        columnHeaderHeight={56}
                        onFilterModelChange={setFilterModel}
                        filterModel={filterModel}
                        sx={sx}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default WorkOrderTable;
